import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { capabilityTooltip, hasFrontendPermission } from '../permissions';

const ROLE_GUIDE = Object.freeze({
  admin: { label: 'Admin', summary: 'Full access to all pages, settings, and account management.' },
  manager: { label: 'Manager', summary: 'Operations access for dispatch, jobs, reports, and team planning.' },
  dispatcher: { label: 'Dispatcher', summary: 'Day-to-day scheduling, jobs, customers, and field coordination.' },
  technician: { label: 'Technician', summary: 'Assigned job execution, check-in/check-out, notes, and updates.' },
  client: { label: 'Client', summary: 'Customer portal access to own jobs and invoices only.' },
});

const ACCOUNT_STATUS_GUIDE = Object.freeze({
  active: 'Can sign in and use assigned pages.',
  disabled: 'Access turned off by admin.',
  locked: 'Temporarily blocked until admin reactivates.',
  invited: 'Account exists but is not yet activated.',
});

const formatRoleLabel = (role) => ROLE_GUIDE[String(role || '').toLowerCase()]?.label || String(role || 'Unknown');
const roleSummary = (role) => ROLE_GUIDE[String(role || '').toLowerCase()]?.summary || 'No role summary available.';
const statusSummary = (status) => ACCOUNT_STATUS_GUIDE[String(status || '').toLowerCase()] || 'No status summary available.';

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function UsersStat({ label, value, tone = 'default' }) {
  return (
    <article className={`users-stat users-stat--${tone}`}>
      <span className="users-stat-label">{label}</span>
      <strong className="users-stat-value">{value}</strong>
    </article>
  );
}

export function UsersPage() {
  const navigate = useNavigate();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageAccounts = hasFrontendPermission(user, 'accounts.manage');

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingUserId, setWorkingUserId] = useState('');
  const [statusDrafts, setStatusDrafts] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadUsers = useCallback(async () => {
    if (!token || !canManageAccounts) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/users', { token });
      const nextUsers = Array.isArray(data) ? data : [];
      setUsers(nextUsers);
      setStatusDrafts(Object.fromEntries(nextUsers.map((entry) => [entry.id, entry.account_status || 'active'])));
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token, canManageAccounts]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshKey]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((entry) => (entry.account_status || 'active') === 'active').length,
    locked: users.filter((entry) => entry.account_status === 'locked').length,
    invited: users.filter((entry) => entry.account_status === 'invited').length,
  }), [users]);

  const applyStatus = async (userId) => {
    if (!token || !canManageAccounts) return;
    setWorkingUserId(userId);
    setError('');
    try {
      const updated = await apiFetch(`/api/users/${encodeURIComponent(userId)}/account-status`, {
        token,
        method: 'PATCH',
        body: { status: statusDrafts[userId] || 'active' },
      });
      setUsers((prev) => prev.map((entry) => entry.id === updated.id ? { ...entry, ...updated } : entry));
    } catch (e) {
      setError(e.message || 'Failed to update account status');
    } finally {
      setWorkingUserId('');
    }
  };

  if (!canManageAccounts) {
    return (
      <section className="users-page">
        <ErrorState title="Access Restricted" message={capabilityTooltip('accounts.manage')} />
      </section>
    );
  }

  if (loading) {
    return (
      <section className="users-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="users-page">
      <div className="users-header">
        <div>
          <span className="users-eyebrow">Admin</span>
          <h1 className="users-heading">Users</h1>
          <p className="users-subheading">
            Role overview, account status control, and access guidance for every account in the workspace.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>
          <RefreshIcon />
          Refresh
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={loadUsers} /> : null}

      <div className="users-stats-grid">
        <UsersStat label="Total Users" value={stats.total} />
        <UsersStat label="Active" value={stats.active} tone="accent" />
        <UsersStat label="Locked" value={stats.locked} tone="danger" />
        <UsersStat label="Invited" value={stats.invited} tone="info" />
      </div>

      <div className="users-layout">
        <Card title="Role Guide">
          <div className="users-guide-list">
            {Object.entries(ROLE_GUIDE).map(([role, info]) => (
              <div className="users-guide-item" key={role}>
                <strong>{info.label}</strong>
                <p>{info.summary}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Account Status Guide">
          <div className="users-guide-list">
            {Object.entries(ACCOUNT_STATUS_GUIDE).map(([status, summary]) => (
              <div className="users-guide-item" key={status}>
                <strong>{status}</strong>
                <p>{summary}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Accounts">
        {users.length ? (
          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.username}</td>
                    <td>{formatRoleLabel(entry.role)}</td>
                    <td>{roleSummary(entry.role)}</td>
                    <td>
                      <span className={`users-status-badge users-status-badge--${entry.account_status || 'active'}`}>
                        {entry.account_status || 'active'}
                      </span>
                      <div className="users-status-help">{statusSummary(entry.account_status || 'active')}</div>
                    </td>
                    <td>
                      <div className="users-inline-edit">
                        <select
                          className="users-select"
                          value={statusDrafts[entry.id] || entry.account_status || 'active'}
                          onChange={(event) => setStatusDrafts((prev) => ({ ...prev, [entry.id]: event.target.value }))}
                        >
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                          <option value="locked">Locked</option>
                          <option value="invited">Invited</option>
                        </select>
                        <Button size="sm" onClick={() => applyStatus(entry.id)} disabled={workingUserId === entry.id}>
                          {workingUserId === entry.id ? 'Saving' : 'Apply'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No users found" message="No accounts are available in the current workspace." />
        )}
      </Card>
    </section>
  );
}
